import { kbLog } from '../../utils/ui/keyboardReframeDebug';
import { hasLiveInlineSpacer, measureWithoutSpacer } from '../../utils/ui/inlineKeyboardSpacer';
import { withoutHeightTransitions } from './modalMeasure';

/**
 * Pin a modal's height at the moment of a tap (RD-DEV-020 part 1).
 *
 * A tall, curtain-bound card whose content FITS is content-hugging: the
 * inline keyboard guard cannot manufacture scroll range in its body (bottom
 * padding inflates the card, its HUG-CONTENT probe reverts), so iOS pans the
 * whole app and the root-scroll pin snaps it back — a visible lurch. Pinning
 * `height` to the current offsetHeight (a visual no-op) makes the body a
 * fixed box, so the guard's existing spacer + scroll reveal works as in the
 * builders, and its own restore machinery returns the layout.
 *
 * Release ONLY after the guard has dropped its spacer
 * (`hasLiveInlineSpacer()` is false) — unfreezing earlier would let the
 * padding inflate the card (the 785px white-void bug). The release TWEENS
 * to the natural height when content changed while pinned (never a snap),
 * with a timer backup because iOS drops transitionend.
 *
 * A pin otherwise lasts until keyboard close / blur: the hook calls
 * `onKeyboardClose()` on keyboard close AND when focus leaves the dialog.
 *
 * Rotation releases IMMEDIATELY (no tween, no spacer wait): the size-family
 * cap just changed, and a pinned px height must never hold the card above
 * its family `maxHeight`. Keyed on a WIDTH change, not any resize — iOS can
 * fire `resize` when the keyboard opens, and that must not unpin mid-typing.
 */

/** No keyboard arrived within this long → release (iPad hardware keyboard etc.). */
const NO_KEYBOARD_MS = 1200;
const POLL_MS = 100;
/** After this, keep waiting but poll slowly — never unfreeze over a live spacer. */
const SLOW_AFTER_MS = 3000;
const SLOW_POLL_MS = 500;
/** KB_MOTION is 250ms; the backup outlives it so the tween lands first. */
const TWEEN_BACKUP_MS = 250 + 80;

function prefersReducedMotion() {
  return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)')?.matches);
}

export function findBodyScroller(dialogEl, fieldEl) {
  let node = fieldEl && fieldEl.parentElement;
  while (node && node !== dialogEl && dialogEl.contains(node)) {
    const oy = window.getComputedStyle(node).overflowY;
    if (oy === 'auto' || oy === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

/** True when the field's body scroller already overflows (no scroller → false). */
export function bodyScrollerOverflows(dialogEl, fieldEl) {
  const scroller = findBodyScroller(dialogEl, fieldEl);
  return !!scroller && scroller.scrollHeight > scroller.clientHeight + 1;
}

export function createHeightFreeze(dialogEl, { onRotate } = {}) {
  let state = null; // { prevHeight }
  let tween = null; // { prevHeight, onEnd, timer }
  let noKbTimer = null;
  let pollTimer = null;
  let pinnedWidth = 0;

  const clearTimers = () => {
    clearTimeout(noKbTimer);
    clearTimeout(pollTimer);
    noKbTimer = null;
    pollTimer = null;
  };

  // A resize/rotation changes the size-family cap itself: the pin must never
  // hold the card above it, so release IMMEDIATELY (no tween, no spacer wait).
  let listening = false;
  const onResize = () => {
    // iOS can fire `resize` when the keyboard opens (width unchanged): that
    // must not unpin mid-typing. A real resize/rotation changes the width.
    if (window.innerWidth === pinnedWidth) return;
    clearTimers();
    if (tween) finishTween();
    restore(0, false);
    if (onRotate) onRotate();
  };
  const attach = () => {
    if (listening) return;
    listening = true;
    pinnedWidth = window.innerWidth;
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  };
  const idle = () => {
    if (!listening || state || tween) return;
    listening = false;
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
  };

  function finishTween() {
    if (!tween) return;
    const t = tween;
    tween = null;
    clearTimeout(t.timer);
    if (t.onEnd) dialogEl.removeEventListener('transitionend', t.onEnd);
    dialogEl.style.height = t.prevHeight;
    idle();
  }

  // A new freeze / keyboard-open mid-tween: stay pinned at wherever the
  // card is right now (visual no-op), keep the saved inline values.
  const cancelTween = () => {
    if (!tween) return;
    const t = tween;
    tween = null;
    clearTimeout(t.timer);
    if (t.onEnd) dialogEl.removeEventListener('transitionend', t.onEnd);
    dialogEl.style.height = `${dialogEl.offsetHeight}px`;
    state = { prevHeight: t.prevHeight };
    pinnedWidth = window.innerWidth; // re-pinned: listeners stay bound, width re-recorded
  };

  const restoreInner = (waited, animate) => {
    if (!state) return;
    const { prevHeight } = state;
    state = null;
    kbLog('unfreeze', { waited });
    if (!animate || prefersReducedMotion()) {
      dialogEl.style.height = prevHeight;
      return;
    }
    // One synchronous pass, no paint: read the natural height, put the pin back.
    const pinned = dialogEl.style.height;
    const natural = readNatural(prevHeight);
    const pinnedPx = parseFloat(pinned);
    if (!(Math.abs(natural - pinnedPx) > 1)) {
      dialogEl.style.height = prevHeight;
      return;
    }
    // The shell's composed transition list animates the height change.
    const onEnd = (e) => {
      if (e.propertyName === 'height' && e.target === dialogEl) finishTween();
    };
    dialogEl.addEventListener('transitionend', onEnd);
    tween = { prevHeight, onEnd, timer: setTimeout(finishTween, TWEEN_BACKUP_MS) };
    dialogEl.style.height = `${natural}px`;
  };

  function restore(waited, animate) {
    restoreInner(waited, animate);
    idle();
  }

  const scheduleRelease = () => {
    if (!state || pollTimer) return;
    clearTimeout(noKbTimer);
    noKbTimer = null;
    let waited = 0;
    const tick = () => {
      pollTimer = null;
      if (!state) return;
      if (!hasLiveInlineSpacer()) {
        restore(waited, true);
        return;
      }
      // Never release over a live spacer (it would inflate the card); a
      // stuck spacer just keeps the card at its pinned height until the
      // guard drops it or the modal unmounts (dispose).
      const next = waited >= SLOW_AFTER_MS ? SLOW_POLL_MS : POLL_MS;
      waited += next;
      pollTimer = setTimeout(tick, next);
    };
    pollTimer = setTimeout(tick, POLL_MS);
  };

  // Natural (unpinned, spacer-free) height: unpin, read, repin in ONE
  // synchronous pass — no paint. The spacer's padding is suspended too, or a
  // content-hugging card would read as inflated by it.
  const readNatural = (prevHeight) => measureWithoutSpacer(() => withoutHeightTransitions(dialogEl, () => {
    const pinned = dialogEl.style.height;
    dialogEl.style.height = prevHeight;
    const natural = dialogEl.offsetHeight;
    dialogEl.style.height = pinned;
    return natural;
  }));

  return {
    isFrozen: () => state != null || tween != null,
    /**
     * `original` ({height}) — the card's true prior inline height
     * when another pin (the step tuck) already holds the height. Returns true
     * when the freeze armed.
     */
    freeze(fieldEl, original) {
      if (!dialogEl) return false;
      if (tween) {
        cancelTween();
        clearTimers();
        noKbTimer = setTimeout(scheduleRelease, NO_KEYBOARD_MS);
        return true;
      }
      if (state) return false;
      const scroller = findBodyScroller(dialogEl, fieldEl);
      if (!scroller) return false;
      if (scroller.scrollHeight > scroller.clientHeight + 1) return false; // guard can already reveal
      state = {
        prevHeight: original ? original.height : dialogEl.style.height,
      };
      const h = dialogEl.offsetHeight;
      dialogEl.style.height = `${h}px`;
      kbLog('freeze', { h, ch: scroller.scrollHeight });
      attach();
      noKbTimer = setTimeout(scheduleRelease, NO_KEYBOARD_MS);
      return true;
    },
    onKeyboardOpen() {
      // Also cancels a pending release: a re-tap right after a close keeps
      // the card pinned for the new typing session.
      clearTimers();
      cancelTween();
    },
    onKeyboardClose: scheduleRelease,
    /** Natural height while pinned (null when not pinned). No visual effect. */
    naturalHeight() {
      return state ? readNatural(state.prevHeight) : null;
    },
    /**
     * Coordinated close (KAN-552 1c): start the tween to the natural height NOW,
     * ignoring the guard's spacer (the caller releases it, THEN calls
     * `finishRelease`). No transitionend / timer of its own — the caller's
     * backup timer is the single clock. Returns true when a release is in flight.
     */
    startRelease(measure = (fn) => fn()) {
      if (tween) return true;
      if (!state) return false;
      clearTimers();
      const { prevHeight } = state;
      state = null;
      kbLog('unfreeze', { manual: 1 });
      // `measure` (the tuck's measureUnfolded) wraps ONLY the READ: the height is
      // written OUTSIDE it, after the transitions are back, so it animates.
      const natural = measure(() => readNatural(prevHeight));
      const pinnedPx = parseFloat(dialogEl.style.height);
      tween = { prevHeight, onEnd: null, timer: 0 };
      if (prefersReducedMotion() || !(Math.abs(natural - pinnedPx) > 1)) return true;
      dialogEl.style.height = `${natural}px`; // animated by the shell's list
      return true;
    },
    /** End a `startRelease`: restore the inline height. Idempotent. */
    finishRelease() {
      finishTween();
    },
    /** A re-tap mid-release: stop at the current height (visual no-op), stay pinned. */
    cancelRelease() {
      cancelTween();
    },
    dispose() {
      clearTimers();
      cancelTween();
      restore(0, false);
    },
  };
}
